import type { CascadeOption, CascadeValue } from '@byecode/ui'
import { IconFont } from '@byecode/ui'
import type { CascadeConfig } from '@lighthouse/core'
import React, { useCallback, useMemo, useState } from 'react'
import { useAsyncRetry, useUpdateEffect } from 'react-use'

import { useApplicationContext } from '../../../../../contexts'
import { useAppThemeContext } from '../../../../ApplicationContainer'
import { CascadeDrawer } from '../../../../FieldDrawer'
import { applyFillPickerValue4CssColorValue } from '../../../../FillPicker'
import { SelectPlaceHolder } from '../../../SelectPlaceHolder'
import type { FieldBaseProps } from '../../../types'
import * as CM from '../../commonStyles'
import * as SC from '../styles'

interface CascadeDrawerFieldProps extends FieldBaseProps {
    value: string
    cascadeConfig: CascadeConfig
}

export const CascadeDrawerField: React.FunctionComponent<CascadeDrawerFieldProps> = props => {
    const {
        onChange,
        value: data,
        isControlled,
        config: { placeholder, title = '', breakPoint },
        readOnly,
        cascadeConfig,
        onFetchCascadeOptions
    } = props
    const { cascade } = cascadeConfig ?? {}
    const { theme } = useAppThemeContext()
    const {
        cascadeFieldPointer = '',
        cascadePointer = '',
        cascadeShowFieldPointer = '',
        showPath,
        isLastLevel,
        filter,
        parentFieldPointer = '',
        sortFieldPointer = ''
    } = cascade ?? {}
    const { placeholderColor, fontFamily } = useMemo(
        () => ({
            placeholderColor: applyFillPickerValue4CssColorValue(breakPoint?.design?.placeholderColor?.color, theme.palettes),
            fontFamily: breakPoint?.font?.fontFamily
        }),
        [breakPoint?.design?.placeholderColor?.color, breakPoint?.font?.fontFamily, theme.palettes]
    )
    const [value, setValue] = useState(data)
    const [path, setPath] = useState('')
    const [opened, setOpened] = useState(false)

    useUpdateEffect(() => {
        setValue(data)
    }, [data])

    const { value: options, retry } = useAsyncRetry(async () => {
        const data = await onFetchCascadeOptions?.({
            fieldPointer: cascadeFieldPointer,
            dsId: cascadePointer,
            sortFieldPointer,
            showFieldPointer: cascadeShowFieldPointer,
            filter,
            parentFieldPointer
        })
        return data ?? []
    }, [cascadeFieldPointer, cascadePointer, sortFieldPointer, filter, parentFieldPointer, onFetchCascadeOptions])

    const { pageTarget } = useApplicationContext()

    const flatOptions = useMemo(() => {
        function getChildOption(option: CascadeOption, list: CascadeOption[]) {
            const { value, label, path, labelPath, children = [] } = option
            let arrList = list
            for (const child of children) {
                const newChild = {
                    ...child,
                    path: `${path}/${child.value}`,
                    labelPath: `${labelPath}/${child.label}`,
                    isLast: (child.children ?? []).length === 0
                }
                arrList = [...arrList, newChild, ...getChildOption(newChild, [])]
            }
            return arrList
        }
        return (
            options?.reduce<CascadeOption[]>((pre, cur) => {
                const newOption = { ...cur, path: cur.value, labelPath: cur.label, isLast: (cur.children ?? []).length === 0 }
                return [...pre, newOption, ...getChildOption(newOption, [])]
            }, []) ?? []
        )
    }, [options])

    const tag = useMemo(() => flatOptions.find(option => option.value === value && value), [flatOptions, value])

    const cascadeListValue = useMemo(() => [value], [value])

    const handleChange = useCallback(
        (v: CascadeValue) => {
            const textValue = v.join(',')
            setValue(textValue)
            onChange?.({ type: 'cascade', value: textValue })
            isLastLevel && setOpened(false)
            setPath('')
        },
        [isLastLevel, onChange]
    )

    const onPathChange = useCallback(
        (data: CascadeOption) => {
            setValue('')
            setPath(showPath ? data.labelPath ?? '' : '')
        },
        [showPath]
    )
    const handleClear = useCallback(() => {
        setValue('')
        setPath('')
        onChange?.({ type: 'cascade', value: '' })
    }, [onChange])

    return (
        <>
            <SC.Container type="button" data-field-input data-field-active={opened} data-field-disabled={!!readOnly}>
                <SC.Wrapper
                    onClick={() => {
                        if (!readOnly) {
                            setOpened(true)
                            retry()
                        }
                    }}
                >
                    <SC.TagWrapper>
                        {tag ? (
                            showPath ? (
                                tag.labelPath
                            ) : (
                                tag.label
                            )
                        ) : (
                            <SelectPlaceHolder color={placeholderColor} label={path || placeholder} />
                        )}
                    </SC.TagWrapper>
                </SC.Wrapper>
                {!readOnly && (
                    <CM.IconWrapper data-field-icon>
                        {tag && <IconFont type="CloseCircle" size={16} onClick={handleClear} />}
                        <IconFont type="ArrowDownSmall" size={16} />
                    </CM.IconWrapper>
                )}
            </SC.Container>
            <CascadeDrawer
                title={title}
                value={cascadeListValue}
                options={options}
                showPath={showPath}
                style={{ fontFamily }}
                // isMultiple={canMultipleChoice}
                selectionMode={isLastLevel ? 'leaf-only' : 'any'}
                opened={opened}
                target={pageTarget}
                path={path}
                onPathChange={onPathChange}
                onFinish={handleChange}
                onClose={() => {
                    setOpened(false)
                    setPath('')
                }}
            />
        </>
    )
}
